import { useRifm } from 'rifm';
import { useState, useEffect } from 'react';
import { useLocalizationContext, } from '@contrast-security-inc/design-system-foundations';
import * as messages from './translations';
import { usePrevious } from './usePrevious';
export const determineSeparator = (format) => {
    return format.match(/\W/)?.[0] || '/';
};
export const determineSeparatorIndexes = (format, isFirstSeparatorRemoved) => {
    const separatorIndexes = [];
    const separator = determineSeparator(format);
    format.split('').forEach((char, index) => {
        if (separator === char) {
            separatorIndexes.push(index);
        }
    });
    return [separatorIndexes[0], isFirstSeparatorRemoved ? separatorIndexes[1] - 1 : separatorIndexes[1]];
};
/**
 * Appends the separator while typing new characters
 */
export const appendSeparator = (value, format) => {
    const separatorIndexes = determineSeparatorIndexes(format);
    return separatorIndexes.includes(value.length) ? value + determineSeparator(format) : value;
};
/**
 * Adjusts the separator position after digits have been edited
 */
export const formatDateWithAppend = (dateString, format) => {
    // remove separators
    const digitsArray = (dateString.match(/\d+/g) || []).join('').split('');
    const separator = determineSeparator(format);
    // Add the in-between separators back in; i.e. 0205 becomes 02/05
    const stringWithInterimSeparators = digitsArray
        .reduce((r, v, index) => (determineSeparatorIndexes(format, true).includes(index) ? `${r}${separator}${v}` : `${r}${v}`), '')
        .slice(0, 10);
    // Now readd the last separator if needed
    return dateString.endsWith(separator) ? `${stringWithInterimSeparators}${separator}` : stringWithInterimSeparators;
};
export const parseNewDate = (textValue, date, parseDate, onChange, onError, formatMessage) => {
    if (textValue.length === 10) {
        const newDate = parseDate(textValue);
        if (newDate && newDate.getTime() !== date?.getTime()) {
            const isDateValid = newDate instanceof Date && !isNaN(newDate.getTime());
            if (!isDateValid) {
                onError(formatMessage(messages, 'invalidDate'));
                return;
            }
            else {
                onChange(newDate);
            }
        }
    }
    onError();
};
export const useDateFormatter = ({ value, onChange, onError }) => {
    const { formatDate, parseDate, getDatePlaceholder, formatMessage } = useLocalizationContext();
    const [textValue, setTextValue] = useState(value ? formatDate(value) || '' : '');
    const previousDate = usePrevious(value);
    const format = getDatePlaceholder();
    useEffect(() => {
        const dateFormat = format; // NOSONAR https://github.com/SonarSource/SonarJS/issues/1961
        // update text if date changed from outside
        if (value?.getTime() !== previousDate?.getTime()) {
            if (!value && textValue) {
                // date is cleared
                setTextValue('');
            }
            else if (value && formatDate(value, dateFormat) !== textValue) {
                // new date
                setTextValue(formatDate(value, dateFormat) || '');
            }
        }
    }, [value, previousDate, textValue, formatDate, format]);
    const { onChange: onInputChange, value: inputValue, ...defaultRifmProps } = useRifm({
        append: (newValue) => appendSeparator(newValue, format),
        value: textValue,
        format: (unformattedDate) => formatDateWithAppend(unformattedDate, format),
        onChange: (newTextValue) => {
            setTextValue(newTextValue);
            parseNewDate(newTextValue, value, parseDate, onChange, onError, formatMessage);
        },
        mask: 10 <= textValue.length,
    });
    return {
        ...defaultRifmProps,
        inputValue,
        onInputChange,
    };
};
